import React, { useRef, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import ReactConsoleEmulator from 'react-console-emulator';
import './Terminal.css';

// Custom Terminal wrapper that overrides processCommand to always use wildcard
class Terminal extends ReactConsoleEmulator {
  processCommand = () => {
    this.setState({ processing: true }, async () => {
      const commandResult = { command: null, args: [], rawInput: null, result: null };
      const rawInput = this.terminalInput.current.value;

      if (!this.props.noHistory) this.pushToHistory(rawInput);

      if (!this.props.noEchoBack) {
        const promptLabel = this.props.promptLabel || '$';
        const echoBack = `${promptLabel} ${rawInput}`;
        this.pushToStdout(echoBack, { isEcho: true });
      }

      if (rawInput) {
        const input = rawInput.split(' ');
        const rawCommand = input.splice(0, 1)[0];
        const args = input;

        commandResult.rawInput = rawInput;
        commandResult.command = rawCommand;
        commandResult.args = args;

        // Always use the '*' wildcard command instead of checking commandExists
        const wildcardCmd = this.state.commands['*'];

        if (wildcardCmd) {
          const res = await wildcardCmd.fn(rawInput); // Pass full raw input
          if (res) this.pushToStdout(res);
          commandResult.result = res;
          if (wildcardCmd.explicitExec) await wildcardCmd.fn(rawInput);
        }
      }

      this.setState({ processing: false }, () => {
        this.clearInput();
        if (!this.props.noAutoScroll) this.scrollToBottom();
        if (this.props.commandCallback) this.props.commandCallback(commandResult);
      });
    });
  };
}

function TerminalComponent() {
  const terminalRef = useRef(null);
  const location = useLocation();
  const [isStreaming, setIsStreaming] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [initStatus, setInitStatus] = useState('pending'); // 'pending', 'initializing', 'ready', 'error'

  // Extract sandbox ID from path (e.g., "/abc123" -> "abc123")
  const sandboxId = location.pathname.slice(1);

  // Check authentication on component mount
  useEffect(() => {
    let isMounted = true;
    const checkAuth = async () => {
      try {
        const res = await fetch('/api/auth', {
          method: 'GET',
          cache: 'no-store',
          credentials: 'include',
        });
        if (!res.ok) throw new Error(`Auth check failed: ${res.status}`);
        const data = await res.json();
        if (isMounted) setIsAuthenticated(Boolean(data.authenticated));
      } catch (err) {
        console.error('Auth check error:', err);
        if (isMounted) setIsAuthenticated(false);
      }
    };
    checkAuth();
    return () => {
      isMounted = false;
    };
  }, []);

  // Initialize sandbox as early as possible (pre-warming)
  useEffect(() => {
    if (!sandboxId || isAuthenticated !== true) {
      return; // Wait until we have sandboxId and confirmed authentication
    }

    let isMounted = true;
    const initializeSandbox = async () => {
      if (isMounted) setInitStatus('initializing');

      try {
        const res = await fetch(`/api/sandboxes/${sandboxId}/initialize`, {
          method: 'POST',
          credentials: 'include',
        });

        if (!res.ok) {
          console.warn(`Sandbox initialization returned ${res.status}`);
          if (isMounted) setInitStatus('error');
          return;
        }

        const data = await res.json();
        if (isMounted) {
          console.log(`Sandbox ${data.status}:`, data.message);
          setInitStatus('ready');
        }
      } catch (err) {
        console.error('Sandbox initialization error:', err);
        if (isMounted) setInitStatus('error');
      }
    };

    initializeSandbox();
    return () => {
      isMounted = false;
    };
  }, [sandboxId, isAuthenticated]);

  const handleCommand = async (command) => {
    if (!sandboxId) {
      return 'Error: No sandbox ID in URL';
    }

    if (initStatus !== 'ready') {
      return 'Initializing sandbox... Please wait.';
    }

    if (isStreaming) {
      return 'Please wait for the current response to complete...';
    }

    setIsStreaming(true);
    const terminal = terminalRef.current;

    try {
      const response = await fetch(`/api/sandboxes/${sandboxId}/message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ message: command }),
      });

      if (!response.ok) {
        setIsStreaming(false);
        return `Error: ${response.status} ${response.statusText}`;
      }

      // Handle streaming response
      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        const chunk = decoder.decode(value, { stream: true });

        // Push each chunk to the terminal as it arrives
        if (terminal) {
          terminal.pushToStdout(chunk);
        }
      }

      setIsStreaming(false);
      return ''; // Return empty since we already pushed to stdout
    } catch (error) {
      setIsStreaming(false);
      return `Error: ${error.message}`;
    }
  };

  const commands = {
    '*': {
      fn: handleCommand,
    }
  };

  const handleLoginRedirect = () => {
    try {
      const currentUrl = window.location.href;
      const redirectUrl = `https://accounts.atxp.ai?loginRedirect=${encodeURIComponent(currentUrl)}`;
      window.location.href = redirectUrl;
    } catch (e) {
      console.error('Login redirect error:', e);
    }
  };

  return (
    <div className="terminal-container">
      {isAuthenticated === false && (
        <div className="auth-modal-overlay">
          <div className="auth-modal">
            <h2>Authentication Required</h2>
            <p>
              You need to sign in with your ATXP account to use this agent.
            </p>
            <button onClick={handleLoginRedirect} className="auth-button">
              Sign in with ATXP
            </button>
          </div>
        </div>
      )}
      {isAuthenticated === true && initStatus === 'initializing' && (
        <div className="auth-modal-overlay">
          <div className="auth-modal">
            <h2>Initializing Sandbox</h2>
            <p>
              Preparing your isolated environment...
            </p>
            <div className="loading-spinner"></div>
          </div>
        </div>
      )}
      {isAuthenticated === true && initStatus === 'error' && (
        <div className="auth-modal-overlay">
          <div className="auth-modal">
            <h2>Initialization Error</h2>
            <p>
              Failed to initialize sandbox. Please refresh the page to try again.
            </p>
            <button onClick={() => window.location.reload()} className="auth-button">
              Refresh Page
            </button>
          </div>
        </div>
      )}
      <Terminal
        ref={terminalRef}
        commands={commands}
        welcomeMessage={`
   ╔═════════════════════════════════════════════════════════╗
   ║\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0║
   ║\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0█████╗\u00A0████████╗██╗\u00A0\u00A0██╗██████╗\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0║
   ║\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0██╔══██╗╚══██╔══╝╚██╗██╔╝██╔══██╗\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0║
   ║\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0███████║\u00A0\u00A0\u00A0██║\u00A0\u00A0\u00A0\u00A0╚███╔╝\u00A0██████╔╝\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0║
   ║\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0██╔══██║\u00A0\u00A0\u00A0██║\u00A0\u00A0\u00A0\u00A0██╔██╗\u00A0██╔═══╝\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0║
   ║\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0██║\u00A0\u00A0██║\u00A0\u00A0\u00A0██║\u00A0\u00A0\u00A0██╔╝\u00A0██╗██║\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0║
   ║\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0╚═╝\u00A0\u00A0╚═╝\u00A0\u00A0\u00A0╚═╝\u00A0\u00A0\u00A0╚═╝\u00A0\u00A0╚═╝╚═╝\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0║
   ║\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0║
   ║\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0Claude Agent in the Cloud\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0║
   ║\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0║
   ╚═════════════════════════════════════════════════════════╝
\u00A0
Welcome! After connecting your ATXP account, you can enter
your prompts and commands here as if it were a local
instance of your agent.
\u00A0
`}
        promptLabel=">_"
        style={{
          minHeight: '100vh',
          maxWidth: '100%',
          overflow: 'auto'
        }}
        styleEchoBack="fullInherit"
        noDefaults={true}
        autoFocus={true}
      />
    </div>
  );
}

export default TerminalComponent;
